import React, { useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PrivacyContainer = styled.div`
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', sans-serif;
  color: #333;
  line-height: 1.7;
`;

const PrivacyHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PrivacyTitle = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: #2563eb;
  margin-bottom: 15px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const PrivacySubtitle = styled.p`
  font-size: 20px;
  color: #555;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const LastUpdated = styled.div`
  background-color: #f0f4f8;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 40px;
  text-align: center;
  font-weight: 600;
  color: #2563eb;
  font-size: 18px;
`;

const ContentSection = styled(motion.section)`
  margin-bottom: 40px;
  padding: 30px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  line-height: 1.7;
  font-size: 17px;
  color: #444;

  p {
    margin-bottom: 20px;
  }

  strong {
    color: #2563eb;
    font-weight: 700;
  }

  a {
    color: #2563eb;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Superscript = styled.sup`
  font-size: 0.6em;
  font-weight: 700;
  vertical-align: super;
  line-height: 0;
  color: inherit;
  position: relative;
  top: -0.2em;
`;

const PrivacyPolicy = () => {
  const containerRef = useRef(null);

  // Use useLayoutEffect to scroll immediately before paint
  useLayoutEffect(() => {
    // Scroll window to top immediately
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also scroll container into view if it exists
    if (containerRef.current) {
      try {
        containerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
      } catch (e) {
        // Fallback for older browsers
        containerRef.current.scrollIntoView(true);
      }
    }
  }, []);

  // Also use useEffect as backup after render
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (containerRef.current) {
        try {
          containerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
        } catch (e) {
          // Fallback for older browsers
          containerRef.current.scrollIntoView(true);
        }
      }
    };
    
    // Immediate scroll
    scrollToTop();
    
    // Also try after a small delay
    const timer = setTimeout(scrollToTop, 10);
    const timer2 = setTimeout(scrollToTop, 100);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <PrivacyContainer ref={containerRef}>
      <PrivacyHeader>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <PrivacyTitle>Privacy Policy</PrivacyTitle>
          <PrivacySubtitle>
            How we collect, use, and protect your information
          </PrivacySubtitle>
        </motion.div>
      </PrivacyHeader>

      <LastUpdated
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        Last updated: 10/9/2025
      </LastUpdated>

      <ContentSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <p>
          <strong>M<Superscript>2</Superscript>eBooks, LLC</strong> ("M<Superscript>2</Superscript>e") is based in Texas, USA. More information about M<Superscript>2</Superscript>e is available at <a href="http://m2ebooks.com" target="_blank" rel="noopener noreferrer">http://m2ebooks.com</a>. M<Superscript>2</Superscript>e operates a multimedia digital reading platform, along with associated websites and mobile applications. Our customers use a variety of electronic devices, applications, and services to access and enjoy written and audio content—including ebooks and audio books—collectively referred to as the "Multi-Media eBook Services." To access these services, customers create an account (the "M<Superscript>2</Superscript>e Account") which they use in connection with M<Superscript>2</Superscript>e's products and services. This Privacy Policy explains how we collect, use, and disclose your personal information when you use your M<Superscript>2</Superscript>e Account or engage with any M<Superscript>2</Superscript>e products, services, websites, or applications that link to this Policy—including our author self-publishing services.
        </p>
      </ContentSection>
    </PrivacyContainer>
  );
};

export default PrivacyPolicy;
